import React from 'react';
import styled from 'styled-components';
import {
    Eyebrow,
    Typography as Text,
    VerticalSpacer,
    SectionInnerGridContent,
    AboveFoldScrollIndicator,
    Video,
    CmsButtonProps,
    Button
} from '../../elements';
import {convertToCommonBackgroundVideo} from '../../../utils';
import {TextLink} from '../../elements/links/TextLink';
import {AccentColorOptions} from 'epic-ue-theme';
import {MouseHijack, CmsButtonPropsWithHoverType, Spotlight} from '../../wraps/';
import {WithCaption, WithCaptionProps} from '../../wraps/caption/WithCaption.wrap';
import {AnimateOnViewWrap} from '../../wraps/animation';
import {LocalVideoOptions} from '../../../types';
import {LightboxButtonPropsOptionsArray} from '../../elements/lightbox/LightboxButtonOptions';
import {LightboxButtonOptions} from '../../elements/lightbox/LightboxButtonOptions';

const Styled = styled.div`
    text-align: center;

    h4,
    .paragraph {
        color: ${(p) => p.theme.palette.text.secondary};
    }

    .promo-image {
        ${(p) => p.theme.breakpoints.down('sm')} {
            margin: 0 -${(p) => p.theme.spacing(3)}px;
            .bottom-caption-wrapper {
                padding-right: ${(p) => p.theme.spacing(3)}px;
            }
        }
    }

    .ratio-keeper {
        padding-bottom: 56.25%;
        .video-wrapper {
            position: absolute;
        }
    }
`;

export interface CaseStudyPromoProps {
    accentColor?: AccentColorOptions;
    buttonOptions?: LightboxButtonPropsOptionsArray;
    caption?: WithCaptionProps;
    description?: string;
    mediaOptions?: LocalVideoOptions;
    link?: CmsButtonPropsWithHoverType;
    sectionName?: string;
    title?: string;
    spotlightsDisabled?: boolean;
    button?: CmsButtonProps;
}

export const CaseStudyPromo = ({
    accentColor,
    buttonOptions = [],
    caption,
    description,
    mediaOptions = {},
    link = {},
    sectionName,
    title,
    spotlightsDisabled,
    button
}: CaseStudyPromoProps): JSX.Element | null => {
    const background = convertToCommonBackgroundVideo(mediaOptions);
    const image = background.image;
    if (!title || !image) return null;

    const validBtn = !!(
        buttonOptions.length &&
        buttonOptions[0] &&
        Object.keys(buttonOptions[0]).length
    );

    return (
        <Styled>
            <SectionInnerGridContent>
                <AnimateOnViewWrap translateY={30}>
                    {sectionName && (
                        <>
                            <Eyebrow>
                                <Text variant="h4">{sectionName}</Text>
                            </Eyebrow>
                            <VerticalSpacer variant="eyebrow" />
                        </>
                    )}
                    {title && (
                        <>
                            <TextLink href={link.href} accentColor={accentColor}>
                                <Text variant="h2">{title}</Text>
                            </TextLink>
                            <VerticalSpacer variant="paragraph" />
                        </>
                    )}
                    {description && (
                        <>
                            <Text variant="p">{description}</Text>
                        </>
                    )}
                    {button?.label && button.href && (
                        <>
                            <VerticalSpacer variant="eyebrow" />
                            <Button type={button.type} href={button.href} color={accentColor}>
                                {button.label}
                            </Button>
                        </>
                    )}
                    {validBtn && (
                        <>
                            <VerticalSpacer variant="heading" />
                            <LightboxButtonOptions
                                buttonOptions={buttonOptions}
                                accentColor={accentColor}
                            />
                        </>
                    )}
                </AnimateOnViewWrap>
            </SectionInnerGridContent>
            <VerticalSpacer variant="custom" spacing={5} downSmSpacing={4} />

            <AnimateOnViewWrap translateY={50}>
                {!background.embedSrc && <AboveFoldScrollIndicator translateY={0} forceSmall />}
                <div className="promo-image">
                    <Spotlight
                        image={image}
                        variant="big"
                        shape="oval"
                        tilt="left"
                        offsetRatio={[0.1, -0.2]}
                        optMaxWidth={1600}
                        disable={spotlightsDisabled}>
                        <MouseHijack
                            href={link.href}
                            hoverCursorType={link.hoverCursorType || 'play'}
                            accentColor={accentColor}>
                            {background.embedSrc ? (
                                <div className="ratio-keeper">
                                    <Video mediaOptions={mediaOptions} />
                                </div>
                            ) : (
                                <WithCaption {...caption}>
                                    <div className="ratio-keeper">
                                        <Video mediaOptions={mediaOptions} />
                                    </div>
                                </WithCaption>
                            )}
                        </MouseHijack>
                    </Spotlight>
                </div>
            </AnimateOnViewWrap>
            <VerticalSpacer variant="custom" spacing={15} downSmSpacing={7} />
        </Styled>
    );
};
